import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { CardBody, CardHeader } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import { useNavigate } from "react-router-dom";
import "./index.css";
import {
    openInitialSlectReastaurantModla,
    setSelectedRestaurantDetails,
} from "../../redux/restaurantsSlice";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import BreadCrumbs from "../../components/BreadCrumbs";
import man from "../../assets/icons/man.PNG";
import { auth } from "../../firebase";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail
} from "firebase/auth";
import { toast } from "react-toastify";
import ChangePasswordModal from "../../components/ChangePasswordModal";

const Profile = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [userEmail, setUserEmail] = useState("No Email");
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    useEffect(() => {
        const storedEmail = localStorage.getItem("email");
        if (storedEmail) {
            setUserEmail(storedEmail);
        }
    }, []);

    // Password change handler
    const handleChangePassword = async (data: {
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
    }) => {
        if (data.newPassword !== data.confirmPassword) {
            toast.error("New passwords do not match");
            return false;
        }

        if (data.newPassword.length < 6) {
            toast.error("Password must be at least 6 characters long");
            return false;
        }

        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                toast.error("User not authenticated");
                return false;
            }

            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, data.currentPassword);
            
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, data.newPassword);
            toast.success("Password changed successfully!");
            
            setShowChangePasswordModal(false);
            return true;
        } catch (error: any) {
            console.error("Password change error:", error);
            
            let errorMessage = "Failed to change password. Please try again.";
            
            switch (error.code) {
                case 'auth/wrong-password':
                    errorMessage = "Current password is incorrect.";
                    break;
                case 'auth/weak-password':
                    errorMessage = "New password is too weak. Please choose a stronger password.";
                    break;
                case 'auth/requires-recent-login':
                    errorMessage = "For security reasons, please log out and log back in before changing your password.";
                    break;
                case 'auth/too-many-requests':
                    errorMessage = "Too many attempts. Please try again later.";
                    break;
                case 'auth/network-request-failed':
                    errorMessage = "Network error. Please check your connection and try again.";
                    break;
                default:
                    errorMessage = error.message || "An error occurred while changing your password.";
            }
            
            toast.error(errorMessage);
            return false;
        }
    };

    // Password reset via email handler
    const handlePasswordResetEmail = async () => {
        try {
            const user = auth.currentUser;
            if (!user || !user.email) {
                toast.error("User not authenticated");
                return;
            }

            const result = await Swal.fire({
                title: 'Reset Password via Email',
                text: `Send a password reset link to ${user.email}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#590B21',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Send Reset Link',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                await sendPasswordResetEmail(auth, user.email, {
                    url: `${window.location.origin}/sign-in`,
                    handleCodeInApp: false,
                });

                Swal.fire({
                    title: 'Password Reset Email Sent!',
                    html: `
                        <p>We've sent a password reset link to <strong>${user.email}</strong></p>
                        <p>Please check your email and follow the instructions to reset your password.</p>
                        <small class="text-muted">You'll need to log in again after resetting your password.</small>
                    `,
                    icon: 'success',
                    confirmButtonColor: '#590B21',
                });
            }
        } catch (error: any) {
            console.error("Password reset email error:", error);
            
            let errorMessage = "Failed to send password reset email. Please try again.";
            
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = "No user found with this email address.";
                    break;
                case 'auth/invalid-email':
                    errorMessage = "Invalid email address.";
                    break;
                case 'auth/too-many-requests':
                    errorMessage = "Too many requests. Please try again later.";
                    break;
                case 'auth/network-request-failed':
                    errorMessage = "Network error. Please check your connection and try again.";
                    break;
                default:
                    errorMessage = error.message || "An error occurred while sending the reset email.";
            }
            
            toast.error(errorMessage);
        }
    };

    const handaleLogOut = () => {
        Swal.fire({
            title: "Are you sure you want to log out?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#590B21",
            cancelButtonColor: "#9F9F9F",
            confirmButtonText: "Yes, log out",
        }).then((result: any) => {
            if (result.isConfirmed) {
                localStorage.clear();
                dispatch(openInitialSlectReastaurantModla(false));
                dispatch(setSelectedRestaurantDetails(null));
                navigate("/sign-in");
            }
        });
    };

    return (
        <>
            <BreadCrumbs hasSubMenus={false} />
            <div>
                <Card>
                    <CardHeader className="profile-top-information-card-header">
                        Profile 
                        <ButtonComponent titel={"Log Out"} onClick={handaleLogOut} />
                    </CardHeader>
                    <CardBody>
                        <img className="avertaStyle" src={man} alt="Login Cover" />
                        <h5>{userEmail}</h5>
                        
                        {/* Password Management Section */}
                        <div className="password-management-section mt-4">
                            <h6 className="mb-3">Password Management</h6>
                            <div className="d-flex justify-content-left gap-2 flex-wrap">
                                <ButtonComponent
                                    titel="Change Password"
                                    onClick={() => setShowChangePasswordModal(true)}
                                />
                                <ButtonComponent
                                    titel="Reset via Email"
                                    onClick={handlePasswordResetEmail}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Change Password Modal */}
            <ChangePasswordModal
                show={showChangePasswordModal}
                onHide={() => setShowChangePasswordModal(false)}
                onSubmit={handleChangePassword}
            />
        </>
    );
};

export default Profile;
